import { FetchResult } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'components/common/buttons/Button';
import { TextInput } from 'components/inputs/TextInput/TextInput';
import { Spacer } from 'components/layouts/Spacer';
import * as ModalStyles from 'components/legacy/modals/Modal.styles';
import { ErrorMessage } from 'components/typography/ErrorMessage';
import { LoginInput, SigninMutation, SigninMutationResult } from 'generated/graphql';
import { useSignin } from 'lib/useSignin';
import { useSubmitForm } from 'lib/useSubmitForm';
import React from 'react';
import ErrorField from '../fields/ErrorField';
import { schema } from './LoginForm.schema';
import { LoginFormProps, LoginInputForm } from './LoginForm.types';
import { Label } from 'components/typography/Label';

export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess, onForgotPassword, className = '', color }) => {
  const {
    form,
    loading: submitting,
    submitGqlForm,
  } = useSubmitForm<LoginInputForm>({
    shouldUseNativeValidation: true,
    mode: 'onSubmit',
    defaultValues: { username: '', password: '' },
    resolver: yupResolver(schema),
  });
  const signin = useSignin();

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = form;

  const onSubmit = async (values: LoginInput) => {
    const { password, username } = values;
    const data = {
      username,
      password,
    };

    const queries: Promise<FetchResult<SigninMutation>>[] = [];

    queries.push(signin.signin(data));

    const res = await submitGqlForm<SigninMutation, SigninMutationResult>(queries);

    if (res.success) {
      const loginResponse = res?.data?.[0]?.data?.loginUser;
      onLoginSuccess(loginResponse);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="form1" method="post" className={className}>
      <Label size="medium" className="mb-1 block">
        Username / Email
      </Label>
      <TextInput {...register('username')} placeholder="Email / Username" aria-label="username" color={color} />
      <ErrorMessage message={errors?.username?.message} className="mt-2" />
      <Label size="medium" className="mt-6 mb-1 block">
        Password
      </Label>
      <TextInput
        {...register('password')}
        type="password"
        placeholder="Password"
        aria-label="password"
        className="mb-1"
        color={color}
      />
      <ErrorMessage message={errors?.password?.message} className="mt-1 mb-1" />
      <ModalStyles.Link fontSize="14px" color="textGray" underline onClick={onForgotPassword}>
        Forgot your password?
      </ModalStyles.Link>
      <Spacer height={24} />
      <Button
        text="Log In"
        busy={submitting}
        disabled={submitting}
        type="submit"
        variety="primary"
        size="medium"
        fullWidth
        data-cy="LoginFormLoginButton"
      />
      <Spacer height="0.5rem" />
      {errors?.generic?.message && <ErrorField error={errors.generic.message} />}
    </form>
  );
};
